/** Who may enter a screening, and where a guest's request to enter stands. */

export const JOIN_POLICIES = ['approval', 'open'] as const;
export type JoinPolicy = (typeof JOIN_POLICIES)[number];

export interface JoinPolicyDefinition {
  /** A guest waits until a host admits them; otherwise the invite link alone lets them in. */
  readonly requiresApproval: boolean;
}

export const JOIN_POLICY_DEFINITIONS = {
  approval: { requiresApproval: true },
  open: { requiresApproval: false },
} as const satisfies Record<JoinPolicy, JoinPolicyDefinition>;

export const DEFAULT_JOIN_POLICY: JoinPolicy = 'approval';

export const ADMISSION_STATUSES = ['pending', 'admitted', 'denied'] as const;
export type AdmissionStatus = (typeof ADMISSION_STATUSES)[number];

export interface AdmissionStatusDefinition {
  /** May load the room, get RTC credentials and send commands. */
  readonly entersRoom: boolean;
  /** Waiting for a host's decision; the guest's screen keeps checking. */
  readonly awaitsHost: boolean;
}

export const ADMISSION_STATUS_DEFINITIONS = {
  pending: { entersRoom: false, awaitsHost: true },
  admitted: { entersRoom: true, awaitsHost: false },
  denied: { entersRoom: false, awaitsHost: false },
} as const satisfies Record<AdmissionStatus, AdmissionStatusDefinition>;

/** What a host decides about a request, and the status each decision leads to. */
export const ADMISSION_DECISIONS = ['admit', 'deny'] as const;
export type AdmissionDecision = (typeof ADMISSION_DECISIONS)[number];

export const ADMISSION_DECISION_OUTCOMES = {
  admit: 'admitted',
  deny: 'denied',
} as const satisfies Record<AdmissionDecision, AdmissionStatus>;

/** Statuses a decision may be taken from. Admitted people are removed through moderation instead. */
export const ADMISSION_DECIDABLE_FROM = {
  admit: ['pending', 'denied'],
  deny: ['pending'],
} as const satisfies Record<AdmissionDecision, readonly AdmissionStatus[]>;
